import React, { useState, useEffect } from "react";
import { agentService } from "../../services/agentService";
import {
  TruckIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  PhoneIcon,
  CalendarIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";

const DeliveryHistory = () => {
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, today, week, month
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await agentService.getDeliveryHistory();
      setDeliveryHistory(data);
    } catch (error) {
      console.error("Error fetching delivery history:", error);
      setMessage("Error loading delivery history");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredHistory = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    return deliveryHistory?.data?.deliveries?.filter((delivery) => {
      const deliveryDate = new Date(delivery?.date);
      switch (filter) {
        case "today":
          return deliveryDate >= today;
        case "week":
          return deliveryDate >= weekAgo;
        case "month":
          return deliveryDate >= monthAgo;
        default:
          return true;
      }
    });
  };

  const getTotalDeliveries = (history) => {
    return history?.length || 0;
  };

  const getCompletedDeliveries = (history) => {
    return history?.filter((delivery) => delivery.pickupStatus === "Delivered")
      .length || 0;
  };

  const getTotalEarnings = (history) => {
    return history?.reduce((total, delivery) => {
      return total + (delivery.totalProfit || 0);
    }, 0) || 0;
  };

  const getTotalQuantity = (history) => {
    return history?.reduce((total, delivery) => {
      return total + (delivery.totalQuantity || 0);
    }, 0) || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const filteredHistory = getFilteredHistory();
  const totalDeliveries = getTotalDeliveries(filteredHistory);
  const completedDeliveries = getCompletedDeliveries(filteredHistory);
  const totalEarnings = getTotalEarnings(filteredHistory);
  const totalQuantity = getTotalQuantity(filteredHistory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Delivery History
        </h1>
        <p className="text-gray-600">
          Track your delivery history and earnings from completed deliveries.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className="bg-red-100 text-red-700 border border-red-200 p-4 rounded-md">
          <div className="flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            {message}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TruckIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Deliveries
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {totalDeliveries}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {completedDeliveries}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalDeliveries - completedDeliveries}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <TruckIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Quantity
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {totalQuantity} kg
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Earnings
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ₦{totalEarnings?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter and History */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Delivery History
          </h2>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>

        {filteredHistory?.length === 0 ? (
          <div className="text-center py-12">
            <TruckIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No deliveries found
            </h3>
            <p className="text-gray-600">
              {filter === "all"
                ? "You haven't made any deliveries yet."
                : `No deliveries found for the selected time period.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredHistory?.map((delivery) => (
              <DeliveryHistoryCard key={delivery._id} delivery={delivery} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Delivery History Card Component
const DeliveryHistoryCard = ({ delivery }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered":
        return "text-green-600 bg-green-100";
      case "In-Store":
        return "text-yellow-600 bg-yellow-100";
      case "pending":
        return "text-blue-600 bg-blue-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Delivered":
        return <CheckCircleIcon className="h-4 w-4" />;
      case "In-Store":
        return <ClockIcon className="h-4 w-4" />;
      case "pending":
        return <ClockIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">
            Delivery #{delivery._id?.slice(-6) || "N/A"}
          </h3>
          <p className="text-sm text-gray-600">
            {new Date(delivery.createdAt).toLocaleDateString()} at{" "}
            {new Date(delivery.createdAt).toLocaleTimeString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-green-600">
            ₦{delivery.totalProfit?.toFixed(2) || "0.00"}
          </p>
          <p className="text-sm text-gray-600">
            {delivery.totalQuantity || 0}kg • {delivery.materials?.length || 0} materials
          </p>
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
              delivery.pickupStatus
            )}`}
          >
            {getStatusIcon(delivery.pickupStatus)}
            <span className="ml-1 capitalize">
              {delivery.pickupStatus?.replace("_", " ")}
            </span>
          </span>
        </div>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex items-center">
          <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-sm text-gray-600">
            Contact/Report Line : 
          </span>
        </div>
        <div className="flex items-center">
          <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-sm text-gray-600">
            {"08109159339"}
          </span>
        </div>
      </div>

      {/* Delivery Details */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Agent Payment Status:
          </span>
          <span className="text-sm text-gray-600">
            {delivery.agentPaymentStatus || "N/A"}
          </span>
        </div>
      </div>

      {/* Agent Payment Status */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Delivery Details:
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-green-600 hover:text-green-700"
          >
            {expanded ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {expanded && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center">
              <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-700">
                Pickup: {delivery.pickupStatus || "Address not provided"}
              </span>
            </div>
            <div className="flex items-center">
              <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-700">
                Delivery: {delivery.delivery_address || "Address not provided"}
              </span>
            </div>
            
            {/* Materials Details */}
            {delivery.materials && delivery.materials.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Materials Delivered:</h4>
                <div className="space-y-1">
                  {delivery.materials.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">
                        {item.material?.name || 'Unknown Material'}
                      </span>
                      <div className="flex items-center space-x-4">
                        <span className="text-gray-600">{item.quantity}kg</span>
                        <span className="text-green-600 font-medium">
                          ₦{item.profit?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {delivery.notes && (
              <div className="text-sm text-gray-700">
                <span className="font-medium">Notes:</span> {delivery.notes}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bin Information */}
      {delivery.bin_id && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">Bin ID:</span> #
          {delivery.bin_id.slice(-6)}
        </div>
      )}
    </div>
  );
};

export default DeliveryHistory;
